// apiClient.ts

/**
 * apiClient - A lightweight wrapper around Fetch API with:
 *   - Base URL (set in vite.config.js as __API_BASE_URL__)
 *   - Request & response interceptors
 *   - Automatic JSON body handling
 *   - Timeout support (via AbortController)
 *   - Consistent error handling
 *   - Convenience HTTP methods (GET, POST, PUT, DELETE)
 */

// Declare the global variable injected by Vite. This tells TypeScript it's expected to exist.
declare const __API_BASE_URL__: string;

// Custom Error for better HTTP error handling
export class HttpError extends Error {
  status: number;
  response: Response;
  config: RequestOptions;
  body?: unknown;

  constructor(message: string, status: number, response: Response, config: RequestOptions) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.response = response;
    this.config = config;
  }
}

// --- Type Definitions ---

export interface RequestOptions extends RequestInit {
    params?: Record<string, string | number | boolean | (string | number | boolean)[] | undefined>;
    timeout?: number;
    rawResponse?: boolean;
    body?: any; // Allow any body type, as it will be stringified
}

type RequestInterceptor = (config: RequestOptions) => RequestOptions | Promise<RequestOptions>;
type ResponseInterceptor = (response: Response) => Response | Promise<Response>;

const apiClient = (() => {
  const baseURL: string = __API_BASE_URL__;

  const requestInterceptors: RequestInterceptor[] = [];
  const responseInterceptors: ResponseInterceptor[] = [];

  const addRequestInterceptor = (fn: RequestInterceptor) => requestInterceptors.push(fn);
  const addResponseInterceptor = (fn: ResponseInterceptor) => responseInterceptors.push(fn);

  function buildUrl(url: string, params?: Record<string, any>): string {
    if (!params) return url;
    const searchParams = new URLSearchParams();
    
    for (const key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
            const value = params[key];
            if (value === null || value === undefined) continue;
            
            if (Array.isArray(value)) {
                value.forEach(item => {
                    // FastAPI expects multiple values for the same key, or key[] depending on config
                    // Standard FastAPI Query(None) expects multiple same-key entries: ?slug=a&slug=b
                    searchParams.append(key, String(item));
                });
            } else {
                searchParams.set(key, String(value));
            }
        }
    }
    const query = searchParams.toString();
    if (!query) return url;
    return url.includes('?') ? `${url}&${query}` : `${url}?${query}`;
  }

  async function request<T = any>(url: string, options: RequestOptions = {}): Promise<T> {
    const isFormData = options.body instanceof FormData;
    let config: RequestOptions = {
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(options.headers || {})
      },
      ...options
    };

    for (const interceptor of requestInterceptors) {
      try {
        config = (await interceptor(config)) || config;
      } catch (err) {
        console.error('Request interceptor error:', err);
      }
    }

    if (config.body && !(config.body instanceof FormData) && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    let controller: AbortController | undefined, timeoutId: NodeJS.Timeout | undefined;
    if (config.timeout) {
      controller = new AbortController();
      config.signal = controller.signal;
      timeoutId = setTimeout(() => controller!.abort(), config.timeout);
    }

    let response: Response;
    try {
      const fullUrl = url.startsWith('http') ? url : baseURL + url;
      const finalUrl = buildUrl(fullUrl, options.params);
      config.credentials = 'include';
      response = await fetch(finalUrl, config);
    } catch (err: any) {
      // fetch() rejects for aborted/timed-out requests and for failures where no response was
      // received (connection refused, DNS, CORS, or a server that closed the connection
      // mid-request). Preserve aborts so callers can detect them; surface everything else as a
      // generic, localized message instead of the raw English reason ("Failed to fetch").
      if (err?.name === 'AbortError') throw err;
      console.error('Request failed before a response was received:', err);
      throw new Error('Não foi possível concluir a solicitação. Tente novamente.');
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    for (const interceptor of responseInterceptors) {
      try {
        response = (await interceptor(response)) || response;
      } catch (err) {
        console.error('Response interceptor error:', err);
      }
    }

    if (!response.ok) {
      const text = await response.text();
      const error = new HttpError(`HTTP ${response.status}: ${text}`, response.status, response, config);
      try {
        error.body = JSON.parse(text);
      } catch {
        // Non-JSON responses retain the existing error behavior.
      }
      throw error;
    }

    if (options.rawResponse) {
      return response as any; // Type assertion for raw response
    }

    if (response.status === 204) {
      return null as any; // Type assertion for empty response
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text() as any; // Type assertion for text response
  }

  // --- Convenience Methods ---

  const get = <T = any>(url: string, options: Omit<RequestOptions, 'body'> = {}) =>
    request<T>(url, { ...options, method: 'GET' });

  const post = <T = any>(url: string, body: any, options: RequestOptions = {}) =>
    request<T>(url, { ...options, method: 'POST', body });

  const put = <T = any>(url: string, body: any, options: RequestOptions = {}) =>
    request<T>(url, { ...options, method: 'PUT', body });

  const patch = <T = any>(url: string, body: any, options: RequestOptions = {}) =>
    request<T>(url, { ...options, method: 'PATCH', body });

  const del = <T = any>(url: string, options: RequestOptions = {}) =>
    request<T>(url, { ...options, method: 'DELETE' });

  return {
    request,
    get,
    post,
    put,
    patch,
    delete: del,
    addRequestInterceptor,
    addResponseInterceptor
  };
})();

export default apiClient;
